/**
 * AppPermissionsTable Component
 *
 * Data table displaying OAuth apps with sortable columns, risk tier badges,
 * permission counts, relative timestamps, and windowed pagination.
 */

import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckBadgeIcon,
  ClockIcon,
  FlagIcon,
  CheckCircleIcon,
  ShieldExclamationIcon,
  UserMinusIcon,
  KeyIcon,
} from '@heroicons/react/24/outline'
import type { OAuthApp } from './appPermissionsTypes'

interface AppPermissionsTableProps {
  apps: OAuthApp[]
  total: number
  pageNumber: number
  pageSize: number
  totalPages: number
  isLoading: boolean
  onPageChange: (page: number) => void
  onAppClick: (app: OAuthApp) => void
}

/**
 * Editorial tier pill styles. Hex values mirror the TIER_COLOR / TIER_DROPCAP
 * palette already used elsewhere on the App Posture tab so severity reads
 * consistently across sections on a paper canvas.
 */
const tierStyles: Record<string, string> = {
  Critical: 'bg-[#C84B4B]/10 text-[#C84B4B] border border-[#C84B4B]/30',
  High: 'bg-[#C9AD80]/15 text-[#8A6A35] border border-[#C9AD80]/40',
  Medium: 'bg-[#4F86A8]/10 text-[#4F86A8] border border-[#4F86A8]/30',
  Low: 'bg-[#4A9B6E]/10 text-[#4A9B6E] border border-[#4A9B6E]/30',
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function getPageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  if (current <= 3) return [1, 2, 3, 4, '...', total]
  if (current >= total - 2) return [1, '...', total - 3, total - 2, total - 1, total]
  return [1, '...', current - 1, current, current + 1, '...', total]
}

export function AppPermissionsTable({
  apps,
  total,
  pageNumber,
  pageSize,
  totalPages,
  isLoading,
  onPageChange,
  onAppClick,
}: AppPermissionsTableProps) {
  if (isLoading) {
    return <TableSkeleton />
  }

  if (apps.length === 0) {
    return (
      <div className="rounded-[4px] border border-dashed border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] p-12 text-center">
        <p className="text-[color:var(--color-navy,#212938)] text-sm">No applications match your filters.</p>
        <p className="text-[color:var(--color-mute,#6B7280)] text-xs italic mt-1">Try adjusting your search or filter criteria.</p>
      </div>
    )
  }

  const startItem = (pageNumber - 1) * pageSize + 1
  const endItem = Math.min(pageNumber * pageSize, total)

  return (
    <div className="rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead>
            <tr className="border-b border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-cream,#FBF8F2)]">
              <th className="px-5 py-3.5 text-[10px] font-bold text-[color:var(--color-faint,#9CA3AF)] uppercase tracking-[0.18em]">Application</th>
              <th className="px-5 py-3.5 text-[10px] font-bold text-[color:var(--color-faint,#9CA3AF)] uppercase tracking-[0.18em]">Risk</th>
              <th className="px-5 py-3.5 text-[10px] font-bold text-[color:var(--color-faint,#9CA3AF)] uppercase tracking-[0.18em]">Permissions</th>
              <th className="px-5 py-3.5 text-[10px] font-bold text-[color:var(--color-faint,#9CA3AF)] uppercase tracking-[0.18em]">Risk Permissions</th>
              <th className="px-5 py-3.5 text-[10px] font-bold text-[color:var(--color-faint,#9CA3AF)] uppercase tracking-[0.18em]">Activity</th>
              <th className="px-5 py-3.5 text-[10px] font-bold text-[color:var(--color-faint,#9CA3AF)] uppercase tracking-[0.18em]">Review Status</th>
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[color:var(--color-rule,#E6E1D5)]">
            {apps.map(app => (
              <tr
                key={app.id}
                onClick={() => onAppClick(app)}
                className={`transition-colors cursor-pointer ${
                  app.acknowledgmentStatus === 'Approved'
                    ? 'bg-[#4A9B6E]/[0.06] hover:bg-[#4A9B6E]/[0.10] border-l-2 border-l-[#4A9B6E]'
                    : app.acknowledgmentStatus === 'Flagged'
                      ? 'bg-[#C84B4B]/[0.05] hover:bg-[#C84B4B]/[0.08] border-l-2 border-l-[#C84B4B]'
                      : app.acknowledgmentStatus === 'Investigating'
                        ? 'bg-[#C9AD80]/[0.07] hover:bg-[#C9AD80]/[0.12] border-l-2 border-l-[#C9AD80]'
                        : 'hover:bg-[color:var(--color-cream,#FBF8F2)]'
                }`}
              >
                {/* Application */}
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-[4px] bg-[color:var(--color-cream,#FBF8F2)] border border-[color:var(--color-rule,#E6E1D5)] flex items-center justify-center text-sm font-bold text-[color:var(--color-navy,#212938)] flex-shrink-0">
                      {app.displayName[0]}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium text-[color:var(--color-navy,#212938)] truncate max-w-[200px]" title={app.displayName}>
                          {app.displayName}
                        </span>
                        <AcknowledgmentBadge status={app.acknowledgmentStatus} expiresAt={app.acknowledgmentExpiresAt} />
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-xs italic text-[color:var(--color-mute,#6B7280)] truncate max-w-[160px]" title={app.publisherName || undefined}>
                          {app.publisherName || 'Unknown publisher'}
                        </span>
                        {app.isVerifiedPublisher && (
                          <CheckBadgeIcon className="h-3.5 w-3.5 text-[#4A9B6E] flex-shrink-0" title="Verified publisher" />
                        )}
                      </div>
                      {/* Inline status badges for critical indicators */}
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        {app.hasDirectoryRoles && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#C84B4B]/10 text-[#C84B4B] border border-[#C84B4B]/30" title="App has Entra directory role assignments">
                            <ShieldExclamationIcon className="h-3 w-3" />
                            Admin Role
                          </span>
                        )}
                        {!app.isExternalApp && app.hasExpiredCredentials && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#C84B4B]/10 text-[#C84B4B] border border-[#C84B4B]/30" title="App has expired credentials">
                            <KeyIcon className="h-3 w-3" />
                            Expired creds
                          </span>
                        )}
                        {!app.isExternalApp && !app.hasExpiredCredentials && app.hasExpiringCredentials && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#C9AD80]/15 text-[#8A6A35] border border-[#C9AD80]/40" title="App has credentials expiring soon">
                            <KeyIcon className="h-3 w-3" />
                            Expiring creds
                          </span>
                        )}
                        {app.ownerCount === 0 && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[color:var(--color-cream,#FBF8F2)] text-[color:var(--color-mute,#6B7280)] border border-[color:var(--color-rule,#E6E1D5)]" title="No assigned owner">
                            <UserMinusIcon className="h-3 w-3" />
                            No owner
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </td>

                {/* Risk tier badge */}
                <td className="px-5 py-4">
                  <span className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full ${tierStyles[app.overallRiskTier] || 'bg-[color:var(--color-cream,#FBF8F2)] text-[color:var(--color-mute,#6B7280)] border border-[color:var(--color-rule,#E6E1D5)]'}`}>
                    {app.overallRiskTier}
                  </span>
                </td>

                {/* Permissions count with hover tooltip breakdown */}
                <td className="px-5 py-4">
                  <div className="group relative">
                    <span className="text-sm font-medium text-[color:var(--color-navy,#212938)] tabular-nums">
                      {app.totalPermissionCount}
                    </span>
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                      <div className="bg-[color:var(--color-paper,#fff)] border border-[color:var(--color-rule,#E6E1D5)] rounded-[4px] px-3 py-2 text-xs text-[color:var(--color-navy,#212938)] shadow-md whitespace-nowrap">
                        <div>{app.applicationPermissionCount} Application</div>
                        <div>{app.delegatedPermissionCount} Delegated</div>
                      </div>
                    </div>
                  </div>
                </td>

                {/* Permission tier counts */}
                <td className="px-5 py-4">
                  <div className="flex items-center gap-2">
                    {app.criticalPermissionCount > 0 && (
                      <span className="text-xs font-medium text-[#C84B4B] bg-[#C84B4B]/10 px-2 py-0.5 rounded">
                        {app.criticalPermissionCount} Critical
                      </span>
                    )}
                    {app.highPermissionCount > 0 && (
                      <span className="text-xs font-medium text-[#8A6A35] bg-[#C9AD80]/15 px-2 py-0.5 rounded">
                        {app.highPermissionCount} High
                      </span>
                    )}
                    {app.criticalPermissionCount === 0 && app.highPermissionCount === 0 && (
                      <span className="text-xs font-medium text-[#4A9B6E] bg-[#4A9B6E]/10 px-2 py-0.5 rounded">
                        Low risk
                      </span>
                    )}
                  </div>
                </td>

                {/* Activity status badge */}
                <td className="px-5 py-4">
                  <ActivityBadge lastSignIn={app.lastSignInDateTime} isStale={app.isStale} />
                </td>

                {/* Justification / acknowledgment reason */}
                <td className="px-5 py-4 max-w-[280px]">
                  {app.acknowledgmentStatus ? (
                    <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded uppercase tracking-wider ${
                      app.acknowledgmentStatus === 'Approved'
                        ? 'bg-[#4A9B6E]/10 text-[#4A9B6E] border border-[#4A9B6E]/30'
                        : app.acknowledgmentStatus === 'Flagged'
                          ? 'bg-[#C84B4B]/10 text-[#C84B4B] border border-[#C84B4B]/30'
                          : app.acknowledgmentStatus === 'Investigating'
                            ? 'bg-[#C9AD80]/15 text-[#8A6A35] border border-[#C9AD80]/40'
                            : 'bg-[color:var(--color-cream,#FBF8F2)] text-[color:var(--color-mute,#6B7280)] border border-[color:var(--color-rule,#E6E1D5)]'
                    }`}>
                      {app.acknowledgmentStatus}
                    </span>
                  ) : (
                    <span className="text-xs text-[color:var(--color-faint,#9CA3AF)] italic">Not reviewed</span>
                  )}
                </td>

                {/* Actions */}
                <td className="px-5 py-4">
                  <button
                    className="p-2 text-[color:var(--color-mute,#6B7280)] hover:text-[color:var(--color-navy,#212938)] hover:bg-[color:var(--color-cream,#FBF8F2)] rounded-[4px] transition-colors"
                    onClick={(e) => { e.stopPropagation(); onAppClick(app) }}
                    aria-label={`View details for ${app.displayName}`}
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-5 py-3.5 border-t border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-cream,#FBF8F2)]">
          <span className="text-xs italic text-[color:var(--color-mute,#6B7280)]">
            Showing {startItem}-{endItem} of {total}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => onPageChange(pageNumber - 1)}
              disabled={pageNumber <= 1}
              className="p-1.5 rounded-[4px] text-[color:var(--color-mute,#6B7280)] hover:text-[color:var(--color-navy,#212938)] hover:bg-[color:var(--color-paper,#fff)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              aria-label="Previous page"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            {getPageNumbers(pageNumber, totalPages).map((page, idx) =>
              page === '...' ? (
                <span key={`ellipsis-${idx}`} className="px-2 text-xs text-[color:var(--color-faint,#9CA3AF)]">
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => onPageChange(page)}
                  className={`min-w-[32px] h-8 rounded-[4px] text-xs font-medium tabular-nums transition-colors
                    ${page === pageNumber
                      ? 'bg-[#212938] text-white border border-[#212938]'
                      : 'text-[color:var(--color-mute,#6B7280)] hover:text-[color:var(--color-navy,#212938)] hover:bg-[color:var(--color-paper,#fff)]'
                    }`}
                >
                  {page}
                </button>
              )
            )}
            <button
              onClick={() => onPageChange(pageNumber + 1)}
              disabled={pageNumber >= totalPages}
              className="p-1.5 rounded-[4px] text-[color:var(--color-mute,#6B7280)] hover:text-[color:var(--color-navy,#212938)] hover:bg-[color:var(--color-paper,#fff)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              aria-label="Next page"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function ActivityBadge({ lastSignIn, isStale }: { lastSignIn?: string | null; isStale: boolean }) {
  if (!lastSignIn) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded bg-[color:var(--color-cream,#FBF8F2)] text-[color:var(--color-mute,#6B7280)] border border-[color:var(--color-rule,#E6E1D5)]">
        Unknown
      </span>
    )
  }

  if (isStale) {
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded bg-[#C9AD80]/15 text-[#8A6A35] border border-[#C9AD80]/40"
        title={`Last sign-in: ${formatDate(lastSignIn)}`}
      >
        Stale
      </span>
    )
  }

  return (
    <span
      className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded bg-[#4A9B6E]/10 text-[#4A9B6E] border border-[#4A9B6E]/30"
      title={`Last sign-in: ${formatDate(lastSignIn)}`}
    >
      Active
    </span>
  )
}

function AcknowledgmentBadge({ status, expiresAt }: { status?: string; expiresAt?: string }) {
  if (!status) return null

  // Check if the acknowledgment review has expired
  const isExpired = expiresAt ? new Date(expiresAt) < new Date() : false
  if (isExpired) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#C9AD80]/15 text-[#8A6A35] border border-[#C9AD80]/40 flex-shrink-0" title="Approval review has expired">
        <ClockIcon className="h-3 w-3" />
        Review expired
      </span>
    )
  }

  switch (status) {
    case 'Approved':
      return <CheckCircleIcon className="h-4 w-4 text-[#4A9B6E] flex-shrink-0" title="Approved" />
    case 'Investigating':
      return <ClockIcon className="h-3.5 w-3.5 text-[#C9AD80] flex-shrink-0" title="Under investigation" />
    case 'Flagged':
      return <FlagIcon className="h-3.5 w-3.5 text-[#C84B4B] flex-shrink-0" title="Flagged for review" />
    default:
      return null
  }
}

function TableSkeleton() {
  return (
    <div className="rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] overflow-hidden">
      <div className="px-5 py-3.5 border-b border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-cream,#FBF8F2)]">
        <div className="flex gap-16">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="h-3 w-20 bg-[color:var(--color-rule,#E6E1D5)] rounded animate-pulse" />
          ))}
        </div>
      </div>
      <div className="divide-y divide-[color:var(--color-rule,#E6E1D5)]">
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className="px-5 py-4 flex gap-8 animate-pulse">
            <div className="flex flex-col gap-2 flex-1">
              <div className="h-4 w-32 bg-[color:var(--color-rule,#E6E1D5)] rounded" />
              <div className="h-3 w-20 bg-[color:var(--color-rule,#E6E1D5)] rounded" />
            </div>
            <div className="h-6 w-16 bg-[color:var(--color-rule,#E6E1D5)] rounded-full" />
            <div className="h-4 w-10 bg-[color:var(--color-rule,#E6E1D5)] rounded" />
            <div className="h-5 w-28 bg-[color:var(--color-rule,#E6E1D5)] rounded" />
            <div className="h-4 w-16 bg-[color:var(--color-rule,#E6E1D5)] rounded" />
            <div className="flex flex-col gap-1.5 flex-1">
              <div className="h-3 w-full bg-[color:var(--color-rule,#E6E1D5)] rounded" />
              <div className="h-3 w-3/4 bg-[color:var(--color-rule,#E6E1D5)] rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
